import React, { useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
} from "reactstrap";
import { Link } from "react-router-dom";
import TiempoProcesamientoWidget from "../../../Components/Common/TiempoProcesamientoWidget";

// Función para formatear detalle de medicamentos con saltos de línea
const formatDetalleMedicamentos = (detalle) => {
  if (!detalle) return "No disponible";
  
  // Dividir por ✔️ o ❌ manteniendo el emoji al inicio de cada línea
  return detalle.split(/(?=✔️|❌)/).map(line => line.trim()).filter(line => line).join('\n');
};

// Función de formateo de fecha local
const formatDate = (dateString) => {
  if (!dateString) return "No disponible";

  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Solicitud = ({ solicitud, renderOnlyEfectividad = false, renderOnlyRest = false }) => {
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

  // Si solo se debe renderizar Efectividad
  if (renderOnlyEfectividad) {
    return (
      <React.Fragment>
        {/* Efectividad de la Entrega - Mostrar para Agudas y Crónicas (excepto Entregado y Cancelado) */}
        {(solicitud?.tipo === "Aguda" || solicitud?.tipo === "Cronica") && 
         !["Entregado", "Cancelado"].includes(solicitud?.estatus) && (
        <Col>
          <Card>
            <CardHeader>
              <div className="d-flex align-items-center">
                <i className="ri-timer-line text-primary me-2 fs-20"></i>
                <h5 className="card-title mb-0">Efectividad de la Entrega</h5>
              </div>
            </CardHeader>
            <CardBody>
              <TiempoProcesamientoWidget
                identificador={solicitud?.identificador}
              />
            </CardBody>
          </Card>
        </Col>
        )}
      </React.Fragment>
    );
  }

  // Si solo se debe renderizar el resto (sin Efectividad)
  if (renderOnlyRest) {
    return (
      <React.Fragment>
        {/* Calificación del Servicio */}
        {(() => {
          const calificaciones = solicitud?.calificaciones?.length > 0
            ? solicitud.calificaciones
            : solicitud?.calificacion 
              ? [solicitud.calificacion]
              : [];
          
          if (calificaciones.length === 0) return null;
          
          return (
            <Col>
              <Card>
                <CardHeader>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <i className="ri-star-line text-primary me-2 fs-20"></i>
                      <h5 className="card-title mb-0">
                        Calificación{calificaciones.length > 1 ? 'es' : ''} del Servicio
                      </h5>
                    </div>
                    <Badge color="info" className="fs-12">
                      {calificaciones.length} {calificaciones.length === 1 ? 'calificación' : 'calificaciones'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="timeline-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {calificaciones.map((cal, index) => (
                      <div 
                        key={index} 
                        className={`timeline-item p-3 mb-3 border rounded ${index === 0 ? 'bg-light-subtle' : ''}`}
                        style={{ width: '100%' }}
                      >
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 gap-2">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Badge color="primary" className="fs-11">#{index + 1}</Badge>
                            {cal.migrada && <Badge color="secondary" className="fs-11"><i className="ri-database-2-line me-1"></i>Migrada</Badge>}
                            {cal.delivery_id && <Badge color="info" className="fs-11"><i className="ri-truck-line me-1"></i>Entrega</Badge>}
                          </div>
                          {cal.creado && <small className="text-muted text-nowrap">{formatDate(cal.creado)}</small>}
                        </div>
                        <div className="mb-2">
                          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                            <span className="fw-medium text-muted text-nowrap">Valoración:</span>
                            <div className="d-flex align-items-center">
                              {[...Array(5)].map((_, starIndex) => (
                                <i key={starIndex} className={`ri-star-${starIndex < cal.valoracion ? "fill" : "line"} me-1 fs-16`} style={{ color: "#FFD700" }}></i>
                              ))}
                              <span className="ms-2 fw-bold">{cal.valoracion}/5</span>
                            </div>
                          </div>
                        </div>
                        {cal.mensaje && (
                          <div style={{ width: '100%' }}>
                            <span className="fw-medium text-muted">Comentario:</span>
                            <div className="p-2 mt-1 bg-white border rounded" style={{ width: '100%' }}>
                              <small>{cal.mensaje}</small>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })()}

        {/* Información de la Solicitud */}
        <Col>
          <Card>
            <CardHeader>
              <div className="d-flex align-items-center">
                <i className="ri-information-line me-2 fs-20"></i>
                <h5 className="card-title mb-0">Información de la Solicitud</h5>
              </div>
            </CardHeader>
            <CardBody>
              <div className="info-list">
                <div className="info-item d-flex flex-column mb-3">
                  <span className="fw-medium text-muted mb-2">Fecha Creación</span>
                  <div><span className="fw-medium" id="create-date">{formatDate(solicitud.creado)}</span></div>
                </div>
                <div className="info-item d-flex flex-column mb-3">
                  <span className="fw-medium text-muted mb-2">Detalle</span>
                  <div className="p-3 bg-light-subtle border rounded" style={{ whiteSpace: 'pre-line' }}>
                    {formatDetalleMedicamentos(solicitud?.detalle)}
                  </div>
                </div>
                <div className="info-item d-flex flex-column mb-3">
                  <span className="fw-medium text-muted mb-2">Diagnóstico</span>
                  <div className="p-3 bg-light-subtle border rounded">{solicitud?.diagnostico}</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Entrega de Medicamentos */}
        <Col>
          <Card>
            <CardHeader>
              <div className="d-flex align-items-center">
                <i className="ri-medicine-bottle-line text-primary me-2 fs-20"></i>
                <h5 className="card-title mb-0">Entrega de Medicamentos</h5>
              </div>
            </CardHeader>
            <CardBody>
              <div className="p-2">
                <Link to="#" className="d-flex align-items-center text-primary" onClick={(e) => { e.preventDefault(); toggle(); }}>
                  <i className="ri-file-list-3-line me-2"></i>
                  Ver Políticas de Beconsult
                </Link>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Modal de Políticas */}
        <Modal isOpen={modal} toggle={toggle} size="xl" scrollable={true}>
          <ModalHeader toggle={toggle}>
            <div className="d-flex align-items-center">
              <i className="ri-file-list-3-line text-primary fs-24 me-2"></i>
              <div>
                <h5 className="card-title mb-0">Especificación del Proceso Entrega de Medicamentos</h5>
                <small className="text-muted">Fecha: 09/09/2024</small>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <Row>
              <Col md={12}>
                <div className="policy-content">
                  <div className="mb-4 pb-2 border-bottom">
                    <h5 className="mb-3 text-primary">OBJETIVO</h5>
                    <p className="text-muted">Definir el proceso para la entrega oportuna de medicamentos para enfermedades Agudas y Crónicas a los asegurados de Seguros Caracas – Red Beconsult</p>
                  </div>
                  <div className="mb-4 pb-2 border-bottom">
                    <h5 className="mb-3 text-primary">ALCANCE</h5>
                    <p className="text-muted">Aplica únicamente para la cobertura Básica de Gastos Médicos de Beconsult</p>
                  </div>
                  <div className="text-center mt-4">
                    <a href="https://drive.google.com/file/d/1cj9KErh0xxbvOSfmTYuhdVyOvnOLfPgB/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                      <i className="ri-download-2-line me-1"></i>Descargar Documento Completo
                    </a>
                  </div>
                </div>
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      </React.Fragment>
    );
  }

  // Renderizado completo (comportamiento por defecto - para compatibilidad)
  return (
    <React.Fragment>
      {/* Efectividad de la Entrega - Mostrar para Agudas y Crónicas (excepto Entregado y Cancelado) */}
      {(solicitud?.tipo === "Aguda" || solicitud?.tipo === "Cronica") && 
       !["Entregado", "Cancelado"].includes(solicitud?.estatus) && (
      <Col>
        <Card>
          <CardHeader>
            <div className="d-flex align-items-center">
              <i className="ri-timer-line text-primary me-2 fs-20"></i>
              <h5 className="card-title mb-0">Efectividad de la Entrega</h5>
            </div>
          </CardHeader>
          <CardBody>
            <TiempoProcesamientoWidget
              identificador={solicitud?.identificador}
            />
          </CardBody>
        </Card>
      </Col>
      )}
      {/* Calificación del Servicio (condicional) - Soporta múltiples calificaciones */}
      {(() => {
        // Priorizar lista nueva, fallback a campo antiguo
        const calificaciones = solicitud?.calificaciones?.length > 0
          ? solicitud.calificaciones
          : solicitud?.calificacion 
            ? [solicitud.calificacion]
            : [];
        
        if (calificaciones.length === 0) return null;
        
        return (
          <Col>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="ri-star-line text-primary me-2 fs-20"></i>
                    <h5 className="card-title mb-0">
                      Calificación{calificaciones.length > 1 ? 'es' : ''} del Servicio
                    </h5>
                  </div>
                  <Badge color="info" className="fs-12">
                    {calificaciones.length} {calificaciones.length === 1 ? 'calificación' : 'calificaciones'}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                <div className="timeline-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {calificaciones.map((cal, index) => (
                    <div 
                      key={index} 
                      className={`timeline-item p-3 mb-3 border rounded ${index === 0 ? 'bg-light-subtle' : ''}`}
                      style={{ width: '100%' }}
                    >
                      {/* Header compacto */}
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Badge color="primary" className="fs-11">
                            #{index + 1}
                          </Badge>
                          {cal.migrada && (
                            <Badge color="secondary" className="fs-11">
                              <i className="ri-database-2-line me-1"></i>
                              Migrada
                            </Badge>
                          )}
                          {cal.delivery_id && (
                            <Badge color="info" className="fs-11">
                              <i className="ri-truck-line me-1"></i>
                              Entrega
                            </Badge>
                          )}
                        </div>
                        {cal.creado && (
                          <small className="text-muted text-nowrap">
                            {formatDate(cal.creado)}
                          </small>
                        )}
                      </div>

                      {/* Estrellas */}
                      <div className="mb-2">
                        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                          <span className="fw-medium text-muted text-nowrap">Valoración:</span>
                          <div className="d-flex align-items-center">
                            {[...Array(5)].map((_, starIndex) => (
                              <i
                                key={starIndex}
                                className={`ri-star-${
                                  starIndex < cal.valoracion ? "fill" : "line"
                                } me-1 fs-16`}
                                style={{ color: "#FFD700" }}
                              ></i>
                            ))}
                            <span className="ms-2 fw-bold">{cal.valoracion}/5</span>
                          </div>
                        </div>
                      </div>

                      {/* Mensaje */}
                      {cal.mensaje && (
                        <div style={{ width: '100%' }}>
                          <span className="fw-medium text-muted">Comentario:</span>
                          <div className="p-2 mt-1 bg-white border rounded" style={{ width: '100%' }}>
                            <small>{cal.mensaje}</small>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        );
      })()}

      {/* Entrega de Medicamentos */}
      <Col>
        <Card>
          <CardHeader>
            <div className="d-flex align-items-center">
              <i className="ri-medicine-bottle-line text-primary me-2 fs-20"></i>
              <h5 className="card-title mb-0">Entrega de Medicamentos</h5>
            </div>
          </CardHeader>
          <CardBody>
            <div className="p-2">
              <Link
                to="#"
                className="d-flex align-items-center text-primary"
                onClick={(e) => {
                  e.preventDefault();
                  toggle();
                }}
              >
                <i className="ri-file-list-3-line me-2"></i>
                Ver Políticas de Beconsult
              </Link>
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* Información de la Solicitud */}
      <Col>
        <Card>
          <CardHeader>
            <div className="d-flex align-items-center">
              <i className="ri-information-line me-2 fs-20"></i>
              <h5 className="card-title mb-0"> Información de la Solicitud</h5>
            </div>
          </CardHeader>
          <CardBody>
            <div className="info-list">
              <div className="info-item d-flex flex-column mb-3">
                <span className="fw-medium text-muted mb-2">
                  Fecha Creación
                </span>
                <div>
                  <span className="fw-medium" id="create-date">
                    {formatDate(solicitud.creado)}
                  </span>
                </div>
              </div>

              <div className="info-item d-flex flex-column mb-3">
                <span className="fw-medium text-muted mb-2">Detalle</span>
                <div className="p-3 bg-light-subtle border rounded" style={{ whiteSpace: 'pre-line' }}>
                  {formatDetalleMedicamentos(solicitud?.detalle)}
                </div>
              </div>

              <div className="info-item d-flex flex-column mb-3">
                <span className="fw-medium text-muted mb-2">Diagnóstico</span>
                <div className="p-3 bg-light-subtle border rounded">
                  {solicitud?.diagnostico}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* Modal de Políticas */}
      <Modal isOpen={modal} toggle={toggle} size="xl" scrollable={true}>
        <ModalHeader toggle={toggle}>
          <div className="d-flex align-items-center">
            <i className="ri-file-list-3-line text-primary fs-24 me-2"></i>
            <div>
              <h5 className="card-title mb-0">
                Especificación del Proceso Entrega de Medicamentos
              </h5>
              <small className="text-muted">Fecha: 09/09/2024</small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="p-4">
          <Row>
            <Col md={12}>
              <div className="policy-content">
                <div className="mb-4 pb-2 border-bottom">
                  <h5 className="mb-3 text-primary">OBJETIVO</h5>
                  <p className="text-muted">
                    Definir el proceso para la entrega oportuna de medicamentos
                    para enfermedades Agudas y Crónicas a los asegurados de
                    Seguros Caracas – Red Beconsult
                  </p>
                </div>

                <div className="mb-4 pb-2 border-bottom">
                  <h5 className="mb-3 text-primary">ALCANCE</h5>
                  <p className="text-muted">
                    Aplica únicamente para la cobertura Básica de Gastos Médicos
                    de Beconsult
                  </p>
                </div>

                <div className="mb-4 pb-2 border-bottom">
                  <h5 className="mb-3 text-primary">REGLAS GENERALES</h5>
                  <div className="policy-list position-relative ps-4 border-start border-3 border-primary">
                    {[
                      "Todas las solicitudes deben realizarse a través de la App MiSalud yMás",
                      "Las patologías crónicas son aquellas que requieren medicamentos para tratamientos de más de 6 meses, un año e inclusive por tiempo indefinido",
                      "Las patologías agudas son enfermedades de aparición súbita de tratamiento rápido, para un tiempo máximo de seis meses. Si el tratamiento es indicado por un período superior a 30 días, se deben realizar solicitudes mensuales hasta el límite de 6 meses",
                      "Enfermedades Crónicas: El equipo médico revisa y aprueba los medicamentos, la entrega se realizará dentro de los seis (06) días hábiles siguientes a la fecha de solicitud",
                      "Enfermedades Agudas: Una vez revisada la solicitud por parte del proveedor, la entrega se realizará dentro de los dos (2) días hábiles siguientes a la fecha de solicitud",
                      "Los Récipes y/o Informes Médicos tendrán una validez de seis (6) meses",
                      "No se dispensarán fármacos prescritos en Récipes y/o Informes Médicos vencidos o que presenten falla en la Identificación del Asegurado, sello del médico y/o fecha",
                      "Los cambios de tratamientos médico para enfermedades crónicas deberán solicitarse a través de la App, cancelando la solicitud inicial y generando una nueva solicitud acompañadas del nuevo récipe e Informe Médico",
                      "En ausencia o incumplimiento de algunos de los recaudos y/o requisitos exigidos al afiliado, el Proveedor de Servicio de farmacia no deberá dispensar el tratamiento",
                    ].map((item, index) => (
                      <div key={index} className="d-flex mb-4">
                        <div className="me-3">
                          <div className="avatar-xs">
                            <div className="avatar-title rounded-circle bg-primary text-white">
                              {index + 1}
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 text-muted">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4 pb-2 border-bottom">
                  <h5 className="mb-3 text-primary">RECAUDOS REQUERIDOS</h5>
                  <div className="policy-list position-relative ps-4 border-start border-3 border-success">
                    {[
                      "Récipe médico original con sello húmedo del médico tratante",
                      "Informe médico original con sello húmedo del médico tratante (para tratamientos crónicos)",
                      "Copia de la cédula de identidad del asegurado",
                      "Copia del carnet de asegurado vigente",
                    ].map((item, index) => (
                      <div key={index} className="d-flex mb-4">
                        <div className="me-3">
                          <div className="avatar-xs">
                            <div className="avatar-title rounded-circle bg-success text-white">
                              <i className="ri-check-line"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 text-muted">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4 pb-2 border-bottom">
                  <h5 className="mb-3 text-primary">TIEMPOS DE ENTREGA</h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="card border-0 bg-light-subtle">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="flex-shrink-0">
                              <div className="avatar-sm">
                                <div className="avatar-title rounded-circle bg-danger text-white">
                                  <i className="ri-heart-pulse-line"></i>
                                </div>
                              </div>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h5 className="mb-0">Enfermedades Agudas</h5>
                            </div>
                          </div>
                          <p className="text-muted mb-0">
                            <i className="ri-time-line me-1 text-danger"></i>
                            Entrega en <strong>2 días hábiles</strong> después
                            de la aprobación
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-0 bg-light-subtle">
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-3">
                            <div className="flex-shrink-0">
                              <div className="avatar-sm">
                                <div className="avatar-title rounded-circle bg-info text-white">
                                  <i className="ri-calendar-check-line"></i>
                                </div>
                              </div>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h5 className="mb-0">Enfermedades Crónicas</h5>
                            </div>
                          </div>
                          <p className="text-muted mb-0">
                            <i className="ri-time-line me-1 text-info"></i>
                            Entrega en <strong>6 días hábiles</strong> después
                            de la aprobación
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center mt-4">
                  <a
                    href="https://drive.google.com/file/d/1cj9KErh0xxbvOSfmTYuhdVyOvnOLfPgB/view?usp=sharing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <i className="ri-download-2-line me-1"></i>
                    Descargar Documento Completo
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

// Optimización: React.memo para evitar re-renders innecesarios
export default React.memo(Solicitud, (prevProps, nextProps) => {
  return prevProps.solicitud?.id === nextProps.solicitud?.id &&
         prevProps.solicitud?.actualizado === nextProps.solicitud?.actualizado &&
         prevProps.solicitud?.calificacion === nextProps.solicitud?.calificacion &&
         prevProps.solicitud?.calificaciones === nextProps.solicitud?.calificaciones;
});
